import { useEffect, useRef, useState } from "react";
import {
  Box,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Input from "../../../../../../components/Input";
import { string } from "../../../../../../constants/string";
import theme from "../../../../../../theme";
import { useClaimFormStore } from "../../../../../../store/useClaimFormStore";
import ButtonComp from "../../../../../../components/Button";
import { useDeceasedFormStore } from "../../../../../../store/useDeceasedFormStore";
import { useRelationOptions } from "../../../../../../hooks/useRelationOptions";
import { usePolicyNumberStore } from "../../../../../../store/usePolicyNumberStore";
import { getBenefitDetails } from "../../../../../../services/guest";
import Loader from "../../../../../../components/Loader";
import { useToast } from "../../../../../../hooks/useToast";
import { useClaimBenefitStore } from "../../../../../../store/useClaimBenefitStore";
import { useBodyRemovalDetailsStore } from "../../../../../../store/useBodyRemovalDetailsStore";
import { useUserStore } from "../../../../../../store/useUserStore";
import BodyRemovalDetails from "./BodyRemovalDetails";
import trash from "../../../../../../assets/images/trash.svg";
import eye from "../../../../../../assets/images/eye.svg";

type DocumentKey =
  | "deceased_id_document"
  | "claim_form"
  | "death_notification_form"
  | "dha_1663_form" 
  | "dha_1680_home_death_form"
  | "medical_report"
  | "death_certificate"
  | "police_accidental_report"
  | "deceased_young_adult"
  | "other_document";

type DocumentRow = {
  id: string;
  documentName: string;
  documentType: string;
  uploadedBy: string;
  date: string;
  file: File;
};

const COMBINED_DECEASED_DOCUMENT_KEY: DocumentKey = "deceased_id_document";
const COMBINED_DECEASED_DOCUMENT_LABEL = "Combined Deceased Documents";

const DECEASED_DOCUMENT_KEYS = new Set<DocumentKey>([
  "deceased_id_document",
  "claim_form",
  "death_notification_form",
  "dha_1663_form",
  "dha_1680_home_death_form",
  "medical_report",
  "death_certificate",
  "police_accidental_report",
  "deceased_young_adult",
  "other_document",
]);

const STILLBORN_REQUIRED_DOCUMENTS: Array<{
  key: DocumentKey;
  label: string;
}> = [
  { key: "claim_form", label: "Completed Claim Form" },
  { key: "death_notification_form", label: "RMA Life Death Notification Form" },
  { key: "deceased_id_document", label: "Certified Copy of Parent ID" },
  { key: "death_certificate", label: "Department of Home Affairs Death Certificate" },
  { key: "dha_1663_form", label: "DHA1663 Notification of Death" },
];

const isUnnaturalDeath = (value: unknown) => value === true || value === "Yes";

const formatUploadDate = () =>
  new Date().toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const getLivesInsuredValue = (member: any, keys: string[]) => {
  for (const key of keys) {
    const value = member?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const getLivesInsuredSignature = (member: any) =>
  [
    getLivesInsuredValue(member, ["title", "Title"]),
    getLivesInsuredValue(member, ["initials", "Initials"]),
    getLivesInsuredValue(member, ["firstName", "name", "First_Name", "Name", "member_name"]),
    getLivesInsuredValue(member, ["lastName", "surname", "Surname"]),
    getLivesInsuredValue(member, ["relationship", "relation", "BenRelation", "Relation"]),
    getLivesInsuredValue(member, [
      "identificationNumber",
      "idNumber",
      "idNum",
      "ID_Number",
      "ID_num",
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]),
    getLivesInsuredValue(member, ["dateOfBirth", "Date_Of_Birth", "Date_of_Birth", "date_of_birth", "BenDOB"]),
    getLivesInsuredValue(member, ["dateOfDeath", "Date_Of_Death", "Date_of_Death", "date_of_death"]),
  ]
    .filter(Boolean)
    .join("|");

const formatLivesInsuredDate = (value: any) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toISOString().slice(0, 10);
};

export default function DeceasedDetailsForm({
  handleDeceasedNext,
  handleBack,
}: {
  handleDeceasedNext: (data: any) => void;
  handleBack: () => void;
}) {
  const { relationOptions, loading } = useRelationOptions();
  const [isLoading, setIsLoading] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const todayLocal = new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .split("T")[0];
  const [documentRows, setDocumentRows] = useState<DocumentRow[]>([]);
  const documentInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const lastHydratedLivesInsuredRef = useRef("");
  const role = useUserStore((state) => state.role);
  const displayRole = role || "Guest";
  const [errors, setErrors] = useState<{ [key: string]: string }>({
    title: "",
    initials: "",
    firstName: "",
    lastName: "",
    relationship: "",
    dateOfBirth: "",
    dateOfDeath: "",
    causeOfDeath: "",
    identificationNumber: "",
    identificationType: "",
    gestationWeeks: "",
    signsOfLifeAfterDelivery: "",
    intentionalTerminationOfPregnancy: "",
    claim_form: "",
    death_notification_form: "",
    deceased_id_document: "",
    dha_1663_form: "",
    dha_1680_home_death_form: "",
    medical_report: "",
    death_certificate: "",
    other_document: "",
    deceased_young_adult: "",
  });

  const { deceasedDetails, setDeceasedDetails } = useDeceasedFormStore();
  const policyNumber = usePolicyNumberStore((state) => state.policyNumber);
  const { documents, addDocument, removeDocument, selectedLivesInsured } = useClaimFormStore();
  const { showInfoToast } = useToast();

  useEffect(() => {
    if (!Object.keys(documents).length || documentRows.length > 0) {
      return;
    }

    setDocumentRows(
      Object.entries(documents)
        .filter(([key]) => key === COMBINED_DECEASED_DOCUMENT_KEY)
        .map(([key, file]) => ({
          id: key,
          documentName: file.name,
          documentType: COMBINED_DECEASED_DOCUMENT_LABEL,
          uploadedBy: displayRole,
          date: formatUploadDate(),
          file,
        })),
    );
  }, [displayRole, documentRows.length, documents]);

  useEffect(() => {
    if (!selectedLivesInsured) {
      lastHydratedLivesInsuredRef.current = "";
      return;
    }

    const signature = getLivesInsuredSignature(selectedLivesInsured);
    if (!signature || signature === lastHydratedLivesInsuredRef.current) {
      return;
    }

    lastHydratedLivesInsuredRef.current = signature;
    setShowErrors(false);

    const identityNumber = getLivesInsuredValue(selectedLivesInsured, [
      "identificationNumber",
      "idNumber",
      "idNum",
      "ID_Number",
      "ID_num",
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]);
    const passportNumber = getLivesInsuredValue(selectedLivesInsured, [
      "passportNumber",
      "passportNum",
      "PassportNumber",
      "Passport_num",
    ]);

    setDeceasedDetails({
      ...deceasedDetails,
      title:
        getLivesInsuredValue(selectedLivesInsured, ["title", "Title"]) ||
        deceasedDetails.title,
      initials:
        getLivesInsuredValue(selectedLivesInsured, ["initials", "Initials"]) ||
        deceasedDetails.initials,
      firstName:
        getLivesInsuredValue(selectedLivesInsured, [
          "firstName",
          "name",
          "First_Name",
          "Name",
          "member_name",
        ]) || deceasedDetails.firstName,
      lastName:
        getLivesInsuredValue(selectedLivesInsured, ["lastName", "surname", "Surname"]) ||
        deceasedDetails.lastName,
      relationship:
        getLivesInsuredValue(selectedLivesInsured, [
          "relationship",
          "relation",
          "BenRelation",
          "Relation",
        ]) || deceasedDetails.relationship,
      dateOfBirth:
        formatLivesInsuredDate(
          getLivesInsuredValue(selectedLivesInsured, [
            "dateOfBirth",
            "Date_Of_Birth",
            "Date_of_Birth",
            "date_of_birth",
            "BenDOB",
          ]),
        ) || deceasedDetails.dateOfBirth,
      dateOfDeath:
        formatLivesInsuredDate(
          getLivesInsuredValue(selectedLivesInsured, [
            "dateOfDeath",
            "Date_Of_Death",
            "Date_of_Death",
            "date_of_death",
          ]),
        ) || deceasedDetails.dateOfDeath,
      identificationNumber: identityNumber || deceasedDetails.identificationNumber,
      identificationType:
        getLivesInsuredValue(selectedLivesInsured, ["identificationType"]) ||
        (identityNumber && !passportNumber
          ? "ID"
          : passportNumber
            ? "Passport"
            : deceasedDetails.identificationType),
    });
  }, [deceasedDetails, selectedLivesInsured, setDeceasedDetails]);

  const handleChange = (field: string, value: string) => {
    setShowErrors(false);

    const updatedFields: Record<string, any> = { [field]: value };

    if (field === "stillBorn") {
      const isStillBorn = value === "Yes";
      updatedFields.stillBorn = isStillBorn;
      updatedFields.dateOfBirth = isStillBorn ? "" : deceasedDetails.dateOfBirth;

      setErrors((prev) => ({
        ...prev,
        dateOfBirth: "",
        identificationType: "",
        identificationNumber: "",
      }));
    }

      if (field === "isBodyRemovalRequired") {
        updatedFields.isBodyRemovalRequired = value === "Yes";
      }

    if (field === "signsOfLifeAfterDelivery") {
      updatedFields.signsOfLifeAfterDelivery = value === "Yes";
    }

    if (field === "intentionalTerminationOfPregnancy") {
      updatedFields.intentionalTerminationOfPregnancy = value === "Yes";
    }

    setDeceasedDetails(updatedFields);

    if (!showErrors) return;

    setErrors((prev) => {
      const newErrors = { ...prev };
      const trimmed = typeof value === "string" ? value.trim() : value;
      const formatField = (name: string) =>
        name.replace(/([A-Z])/g, " $1").toLowerCase();

      newErrors[field] = !trimmed ? `Please enter ${formatField(field)}` : "";

      if (field === "dateOfBirth") {
        const isValidDate = !Number.isNaN(Date.parse(trimmed));
        newErrors.dateOfBirth = !trimmed
          ? "Please select date of birth"
          : isValidDate
            ? trimmed > todayLocal
              ? "Date of birth cannot be in the future"
              : ""
            : "Invalid date format";
      }

      if (field === "dateOfDeath") {
        const isValidDate = !Number.isNaN(Date.parse(trimmed));
        newErrors.dateOfDeath = !trimmed
          ? "Please select date of death"
          : isValidDate
            ? trimmed > todayLocal
              ? "Date of death cannot be in the future"
              : ""
            : "Invalid date format";
      }

      if (field in newErrors && field.includes("document")) {
        newErrors[field] = trimmed ? "" : "Please upload the document.";
      }

      return newErrors;
    });
  };

  const validateDeathClaimForm = () => {
    const newErrors: { [key: string]: string } = {};
    let isValid = true;
    const { stillBorn, isBodyRemovalRequired } = deceasedDetails;
    const { deceasedLocation } = useBodyRemovalDetailsStore.getState();
    let requiredFields: string[] = [];

    if (stillBorn === true) {
      requiredFields = [
        "firstName",
        "lastName",
        "dateOfDeath",
        "gestationWeeks",
      ];
    } else {
      requiredFields = [
        "title",
        "initials",
        "firstName",
        "lastName",
        "relationship",
        "dateOfBirth",
        "dateOfDeath",
        "causeOfDeath",
        "identificationNumber",
        "identificationType",
      ];
    }

    requiredFields.forEach((field) => {
      const value = deceasedDetails[field as keyof typeof deceasedDetails];
      if (!value) {
        newErrors[field] = `Please enter ${field
          .replace(/([A-Z])/g, " $1")
          .toLowerCase()}`;
        isValid = false;
      }
    });

    if (deceasedDetails.dateOfBirth && deceasedDetails.dateOfBirth > todayLocal) {
      newErrors.dateOfBirth = "Date of birth cannot be in the future";
      isValid = false;
    }

    if (deceasedDetails.dateOfDeath && deceasedDetails.dateOfDeath > todayLocal) {
      newErrors.dateOfDeath = "Date of death cannot be in the future";
      isValid = false;
    }

    if (deceasedDetails.stillBorn !== true) {
      if (!deceasedDetails.dateOfBirth) {
        newErrors.dateOfBirth = "Please select date of birth";
        isValid = false;
      }

      if (isBodyRemovalRequired) {
        const bodyRemovalRequiredFields = [
          "houseNumber",
          "streetName",
          "suburb",
          "city",
          "province",
          "postalCode",
          "notes",
        ];

        bodyRemovalRequiredFields.forEach((field) => {
          if (!deceasedLocation?.[field as keyof typeof deceasedLocation]) {
            newErrors[field] = `Please enter ${field
              .replace(/([A-Z])/g, " $1")
              .toLowerCase()}`;
            isValid = false;
          }
        });
      }

      if (!deceasedDetails.dateOfDeath) {
        newErrors.dateOfDeath = "Please select date of death";
        isValid = false;
      }
    }

    if (stillBorn === true) {
      const gestationWeeks = Number(deceasedDetails.gestationWeeks);
      if (!Number.isFinite(gestationWeeks) || gestationWeeks < 26) {
        newErrors.gestationWeeks = "Stillborn claims require at least 26 weeks of pregnancy";
        // Do not set isValid = false to prevent blocking progress
      }

      if (deceasedDetails.signsOfLifeAfterDelivery !== false) {
        newErrors.signsOfLifeAfterDelivery = "Select No to confirm there were no signs of life";
        // Do not set isValid = false to prevent blocking progress
      }

      if (deceasedDetails.intentionalTerminationOfPregnancy !== false) {
        newErrors.intentionalTerminationOfPregnancy = "Select No to confirm this was not intentional termination";
        // Do not set isValid = false to prevent blocking progress
      }
    }

    if (!documents.deceased_id_document) {
      newErrors.deceased_id_document = "Combined deceased documents are required";
      isValid = false;
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return isValid;
  };

  const handleCombinedDocumentSelected = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [COMBINED_DECEASED_DOCUMENT_KEY]: "File must be less than 2MB",
      }));
      return;
    }

    DECEASED_DOCUMENT_KEYS.forEach((key) => {
      if (key !== COMBINED_DECEASED_DOCUMENT_KEY) {
        removeDocument(key);
      }
    });
    addDocument(COMBINED_DECEASED_DOCUMENT_KEY, file);

    setDocumentRows([
      {
        id: COMBINED_DECEASED_DOCUMENT_KEY,
        documentName: file.name,
        documentType: COMBINED_DECEASED_DOCUMENT_LABEL,
        uploadedBy: displayRole,
        date: formatUploadDate(),
        file,
      },
    ]);

    setErrors((prev) => ({
      ...prev,
      [COMBINED_DECEASED_DOCUMENT_KEY]: "",
    }));
  };

  const handleStillbornDocumentSelected = (
    documentKey: DocumentKey,
    documentLabel: string,
    files: FileList | null,
  ) => {
    const file = files?.[0];
    if (!file) {
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        [documentKey]: "File must be less than 2MB",
      }));
      return;
    }

    addDocument(documentKey, file);
    setDocumentRows((prev) => [
      ...prev.filter((row) => row.id !== documentKey),
      {
        id: documentKey,
        documentName: file.name,
        documentType: documentLabel,
        uploadedBy: displayRole,
        date: formatUploadDate(),
        file,
      },
    ]);

    setErrors((prev) => ({
      ...prev,
      [documentKey]: "",
    }));
  };

  const handleDeleteDocument = (documentKey: string) => {
    removeDocument(documentKey);
    setDocumentRows((prev) => prev.filter((row) => row.id !== documentKey));
    if (documentInputRefs.current[documentKey]) {
      documentInputRefs.current[documentKey]!.value = "";
    }

    if (documentKey === "deceased_id_document") {
      setErrors((prev) => ({
        ...prev,
        deceased_id_document: deceasedDetails.stillBorn
          ? "Certified Copy of Parent ID is required"
          : "Combined deceased documents are required",
      }));
    }
  };

  const handlePreview = (file: File) => {
    const fileURL = URL.createObjectURL(file);
    window.open(fileURL, "_blank");
  };

  const handleNext = async () => {
    setShowErrors(true);

    if (validateDeathClaimForm()) {
      setIsLoading(true);

      const { deceasedDetails: currentDeceasedDetails } =
        useDeceasedFormStore.getState();
      const { deceasedLocation } = useBodyRemovalDetailsStore.getState();

      const transformedDetails = currentDeceasedDetails.stillBorn
        ? {
            ...currentDeceasedDetails,
            title: currentDeceasedDetails.title || "Mr",
            initials: currentDeceasedDetails.initials || "N/A",
            relationship: currentDeceasedDetails.relationship || "Child",
            identificationType: "",
            identificationNumber: "",
            dateOfBirth: "",
            causeOfDeath: currentDeceasedDetails.causeOfDeath || "Stillbirth",
            gestationWeeks: Number(currentDeceasedDetails.gestationWeeks),
            signsOfLifeAfterDelivery: false,
            intentionalTerminationOfPregnancy: false,
            stillBorn: true,
            isBodyRemovalRequired: false,
          }
        : currentDeceasedDetails;

      const finalPayload = {
        ...transformedDetails,
        deceasedLocation,
      };

      const res = await getBenefitDetails(policyNumber, finalPayload);

      if (res.status === 200) {
        useClaimBenefitStore.getState().setClaimBenefit(res.data);
        useClaimFormStore
          .getState()
          .setIsPolicyUnderWaitingPeriod(res.data.isPolicyUnderWaitingPeriod);
        useClaimFormStore
          .getState()
          .setIsClaimSubmissionWindowExpired(
            res.data.isClaimSubmissionWindowExpired ?? false,
          );
        handleDeceasedNext(finalPayload);
      } else {
        showInfoToast(res.data.message);
      }

      setIsLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      {isLoading ? <Loader isLoading={isLoading} /> : null}
      <form>
        <Grid container spacing={2}>
          {deceasedDetails.stillBorn === true ? (
            <Grid size={12}>
              {/* SECTION 1: INSURED PARENT DETAILS */}
              <Box
                sx={{
                  p: 3,
                  mb: 3,
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  backgroundColor: "#f8fafc",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary"
                  mb={2.5}
                  textAlign="left"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Insured Parent Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      fullWidth
                      label="First Name"
                      variant="outlined"
                      value={deceasedDetails.firstName}
                      onChange={(e: any) => handleChange("firstName", e.target.value)}
                      error={showErrors && Boolean(errors.firstName)}
                      helperText={showErrors ? errors.firstName : ""}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }}>
                    <Input
                      fullWidth
                      label="Surname"
                      variant="outlined"
                      value={deceasedDetails.lastName}
                      onChange={(e: any) => handleChange("lastName", e.target.value)}
                      error={showErrors && Boolean(errors.lastName)}
                      helperText={showErrors ? errors.lastName : ""}
                    />
                  </Grid>

                </Grid>
              </Box>

              {/* SECTION 2: PREGNANCY & DELIVERY DETAILS */}
              <Box
                sx={{
                  p: 3,
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  backgroundColor: "#f8fafc",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  color="primary"
                  mb={2.5}
                  textAlign="left"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.5px" }}
                >
                  Pregnancy & Delivery Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      textAlign="left"
                      color="text.secondary"
                      mb={0.5}
                    >
                      Still Born Claim
                    </Typography>
                    <RadioGroup
                      row
                      value={deceasedDetails.stillBorn ? "Yes" : "No"}
                      onChange={(e) => handleChange("stillBorn", e.target.value)}
                      sx={{ minHeight: 40 }}
                    >
                      <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Input
                      fullWidth
                      type="number"
                      label="Pregnancy Weeks"
                      variant="outlined"
                      value={deceasedDetails.gestationWeeks}
                      onChange={(e: any) => handleChange("gestationWeeks", e.target.value)}
                      error={showErrors && Boolean(errors.gestationWeeks)}
                      helperText={showErrors ? errors.gestationWeeks : ""}
                      inputProps={{ min: 26 }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <Input
                      type="date"
                      fullWidth
                      label={string.deceased.dod}
                      placeholder="DD-MM-YYYY"
                      variant="outlined"
                      value={deceasedDetails.dateOfDeath}
                      onChange={(e: any) => handleChange("dateOfDeath", e.target.value)}
                      error={showErrors && Boolean(errors.dateOfDeath)}
                      helperText={showErrors ? errors.dateOfDeath : ""}
                      inputProps={{
                        max: todayLocal,
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }} textAlign="left">
                    <Typography fontWeight={600} fontSize={15} color="text.secondary" mb={1}>
                      Signs of life after delivery
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        deceasedDetails.signsOfLifeAfterDelivery === null
                          ? ""
                          : deceasedDetails.signsOfLifeAfterDelivery
                            ? "Yes"
                            : "No"
                      }
                      onChange={(e) =>
                        handleChange("signsOfLifeAfterDelivery", e.target.value)
                      }
                    >
                      <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                    {showErrors && errors.signsOfLifeAfterDelivery && (
                      <Typography color="error.main" fontSize={12} sx={{ mt: 0.5 }}>
                        {errors.signsOfLifeAfterDelivery}
                      </Typography>
                    )}
                  </Grid>

                  <Grid size={{ xs: 12, md: 6 }} textAlign="left">
                    <Typography fontWeight={600} fontSize={15} color="text.secondary" mb={1}>
                      Intentional termination of pregnancy
                    </Typography>
                    <RadioGroup
                      row
                      value={
                        deceasedDetails.intentionalTerminationOfPregnancy === null
                          ? ""
                          : deceasedDetails.intentionalTerminationOfPregnancy
                            ? "Yes"
                            : "No"
                      }
                      onChange={(e) =>
                        handleChange("intentionalTerminationOfPregnancy", e.target.value)
                      }
                    >
                      <FormControlLabel value="Yes" control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                    {showErrors && errors.intentionalTerminationOfPregnancy && (
                      <Typography color="error.main" fontSize={12} sx={{ mt: 0.5 }}>
                        {errors.intentionalTerminationOfPregnancy}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ) : (
            <>
              {/* NORMAL DEATH CLAIM FIELDS */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  select
                  fullWidth
                  label="Title"
                  variant="outlined"
                  value={deceasedDetails.title}
                  onChange={(e: any) => handleChange("title", e.target.value)}
                  error={showErrors && Boolean(errors.title)}
                  helperText={showErrors ? errors.title : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Title</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  <MenuItem
                    value="Adv"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    ADV
                  </MenuItem>
                  <MenuItem
                    value="Dr"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    DR
                  </MenuItem>
                  <MenuItem
                    value="Miss"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MISS
                  </MenuItem>
                  <MenuItem
                    value="Mr"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MR
                  </MenuItem>
                  <MenuItem
                    value="Mrs"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MRS
                  </MenuItem>
                  <MenuItem
                    value="Ms"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    MS
                  </MenuItem>
                  <MenuItem
                    value="Prof"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    PROF
                  </MenuItem>
                  <MenuItem
                    value="Rev"
                    sx={{
                      background: "background.default",
                      borderBottom: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    REV
                  </MenuItem>
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  fullWidth
                  label="Initials"
                  variant="outlined"
                  value={deceasedDetails.initials}
                  onChange={(e: any) => handleChange("initials", e.target.value)}
                  error={showErrors && Boolean(errors.initials)}
                  helperText={showErrors ? errors.initials : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  fullWidth
                  label={string.deceased.firstName}
                  variant="outlined"
                  value={(() => {
                    const rawFirstName = deceasedDetails.firstName || "";
                    if (Boolean(deceasedDetails.stillBorn) && rawFirstName.trim()) {
                      return /^baby\s+of\b/i.test(rawFirstName.trim())
                        ? rawFirstName
                        : `Baby of ${rawFirstName.trim()}`;
                    }
                    return rawFirstName;
                  })()}
                  onChange={(e: any) => handleChange("firstName", e.target.value)}
                  error={showErrors && Boolean(errors.firstName)}
                  helperText={showErrors ? errors.firstName : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <Input
                  fullWidth
                  label={string.deceased.lastName}
                  variant="outlined"
                  value={deceasedDetails.lastName}
                  onChange={(e: any) => handleChange("lastName", e.target.value)}
                  error={showErrors && Boolean(errors.lastName)}
                  helperText={showErrors ? errors.lastName : ""}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 2 }}>
                <Typography
                  variant="subtitle1"
                  textAlign="left"
                  fontWeight={600}
                  fontSize={18}
                >
                  Still Born
                </Typography>
                <RadioGroup
                  row
                  value="No"
                  onChange={(e) => handleChange("stillBorn", e.target.value)}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Input
                  type="date"
                  fullWidth
                  label={string.deceased.dob}
                  placeholder="DD-MM-YYYY"
                  variant="outlined"
                  value={deceasedDetails.dateOfBirth}
                  onChange={(e: any) => handleChange("dateOfBirth", e.target.value)}
                  error={showErrors && Boolean(errors.dateOfBirth)}
                  helperText={showErrors ? errors.dateOfBirth : ""}
                  inputProps={{
                    max: todayLocal,
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Input
                  type="date"
                  fullWidth
                  label={string.deceased.dod}
                  placeholder="DD-MM-YYYY"
                  variant="outlined"
                  value={deceasedDetails.dateOfDeath}
                  onChange={(e: any) => handleChange("dateOfDeath", e.target.value)}
                  error={showErrors && Boolean(errors.dateOfDeath)}
                  helperText={showErrors ? errors.dateOfDeath : ""}
                  inputProps={{
                    max: todayLocal,
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }} mt={1.8}>
                <Input
                  select
                  fullWidth
                  label={string.deceased.relationshipToClaimant}
                  variant="outlined"
                  value={deceasedDetails.relationship}
                  onChange={(e: any) =>
                    handleChange("relationship", e.target.value)
                  }
                  error={showErrors && Boolean(errors.relationship)}
                  helperText={showErrors ? errors.relationship : ""}
                  SelectProps={{
                    displayEmpty: true,
                    renderValue: (selected: any) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>Select Relationship</span>
                      ),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          backgroundColor: "#ffffff",
                          borderRadius: "12px",
                          maxHeight: "300px",
                        },
                      },
                    },
                  }}
                >
                  {loading ? (
                    <MenuItem disabled>Loading relationships...</MenuItem>
                  ) : (
                    relationOptions.map((relation: string, index) => (
                      <MenuItem
                        key={index}
                        value={relation}
                        sx={{
                          background: "background.default",
                          borderBottom: `1px solid ${theme.palette.grey[200]}`,
                        }}
                      >
                        {relation}
                      </MenuItem>
                    ))
                  )}
                </Input>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }} mt={1.8}>
                <Input
                  fullWidth
                  label={string.deceased.causeOfDeath}
                  variant="outlined"
                  value={deceasedDetails.causeOfDeath}
                  onChange={(e: any) =>
                    handleChange("causeOfDeath", e.target.value)
                  }
                  error={showErrors && Boolean(errors.causeOfDeath)}
                  helperText={showErrors ? errors.causeOfDeath : ""}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <RadioGroup
                  row
                  value={deceasedDetails.identificationType}
                  onChange={(e) =>
                    handleChange("identificationType", e.target.value)
                  }
                >
                  <FormControlLabel
                    value="ID"
                    control={<Radio />}
                    label="ID"
                  />
                  <FormControlLabel
                    value="Passport"
                    control={<Radio />}
                    label={string.deceased.passport}
                  />
                </RadioGroup>
                <Input
                  fullWidth
                  variant="outlined"
                  placeholder={
                    deceasedDetails.identificationType === "Passport"
                      ? "Passport Number"
                      : "ID Number"
                  }
                  value={deceasedDetails.identificationNumber}
                  onChange={(e: any) =>
                    handleChange("identificationNumber", e.target.value)
                  }
                  error={showErrors && Boolean(errors.identificationNumber)}
                  helperText={showErrors ? errors.identificationNumber : ""}
                />

                {showErrors && errors.identificationType && (
                  <Typography
                    textAlign="left"
                    sx={{ color: "error.main", fontSize: "0.8rem", ml: 1 }}
                  >
                    {errors.identificationType}
                  </Typography>
                )}
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Typography
                  variant="subtitle1"
                  textAlign="left"
                  fontWeight={600}
                  fontSize={18}
                >
                  Benefits Rewards Member Card
                </Typography>
                <RadioGroup
                  row
                  value={deceasedDetails.memberCard || "No"}
                  onChange={(e) => handleChange("memberCard", e.target.value)}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }} textAlign="left">
                <Typography fontWeight={600} fontSize={18} mb={1}>
                  {string.deceased.IsItUnnatural}
                </Typography>
                <RadioGroup
                  row
                  value={deceasedDetails.isNaturalDeath || "No"}
                  onChange={(e) => handleChange("isNaturalDeath", e.target.value)}
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>

              <Grid size={{ xs: 12, md: 4 }} textAlign="left">
                <Typography fontWeight={600} fontSize={18} mb={1}>
                  {string.deceased.IsBodyRemoval}
                </Typography>
                <RadioGroup
                  row
                  value={deceasedDetails.isBodyRemovalRequired ? "Yes" : "No"}
                  onChange={(e) =>
                    handleChange("isBodyRemovalRequired", e.target.value)
                  }
                >
                  <FormControlLabel
                    value="Yes"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="No"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>
            </>
          )}
        </Grid>

        {deceasedDetails.isBodyRemovalRequired && (
          <BodyRemovalDetails errors={errors} showErrors={showErrors} />
        )}

        <Paper
          elevation={0}
          sx={{
            p: 3,
            mt: 3,
            borderRadius: 3,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
          }}
        >
          {(() => {
            const combinedDocument = documentRows.find(
              (row) => row.id === COMBINED_DECEASED_DOCUMENT_KEY,
            );
            const combinedDocumentError = errors[COMBINED_DECEASED_DOCUMENT_KEY];

            return (
              <Box
                sx={{
                  textAlign: "left",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap",
                  }}
                >
                  <Typography fontWeight={700} fontSize={18}>
                    {COMBINED_DECEASED_DOCUMENT_LABEL} *
                  </Typography>
                  <ButtonComp
                    variant="contained"
                    sx={{
                      backgroundColor: combinedDocument ? "#6D6D6D" : "#EF6C00",
                      borderRadius: "24px",
                      height: 42,
                      minWidth: 132,
                      px: 3,
                      fontWeight: 700,
                      "&:hover": {
                        backgroundColor: combinedDocument ? "#5F5F5F" : "#E65100",
                      },
                    }}
                    onClick={() =>
                      documentInputRefs.current[
                        COMBINED_DECEASED_DOCUMENT_KEY
                      ]?.click()
                    }
                  >
                    {combinedDocument ? "REPLACE FILE" : "CHOOSE FILE"}
                  </ButtonComp>
                </Box>

                <Box
                  sx={{
                    border: combinedDocument
                      ? "1px solid #D1D5DB"
                      : "1px dashed #CBD5E1",
                    borderRadius: "12px",
                    backgroundColor: combinedDocument ? "#F9FAFB" : "#FCFCFD",
                    minHeight: 92,
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: { xs: "stretch", sm: "center" },
                    justifyContent: "space-between",
                    gap: 2,
                    flexDirection: { xs: "column", sm: "row" },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "10px",
                        backgroundColor: "#FFF3E8",
                        color: "#EF6C00",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 800,
                        fontSize: 12,
                        flexShrink: 0,
                      }}
                    >
                      DOC
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        fontWeight={700}
                        fontSize={15}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {combinedDocument
                          ? combinedDocument.documentName
                          : "No file selected"}
                      </Typography>
                      <Typography color="#6B7280" fontSize={13} mt={0.25}>
                        {combinedDocument
                          ? `Uploaded by ${combinedDocument.uploadedBy} on ${combinedDocument.date}`
                          : "Upload one PDF or image that contains all deceased documents"}
                      </Typography>
                    </Box>
                  </Box>

                  <input
                    type="file"
                    hidden
                    ref={(element) => {
                      documentInputRefs.current[
                        COMBINED_DECEASED_DOCUMENT_KEY
                      ] = element;
                    }}
                    accept="image/*,.pdf"
                    onChange={(e) =>
                      handleCombinedDocumentSelected(e.target.files)
                    }
                  />
                  {combinedDocument && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: { xs: "flex-start", sm: "flex-end" },
                        gap: 0.5,
                        flexShrink: 0,
                      }}
                    >
                      <IconButton
                        onClick={() => handlePreview(combinedDocument.file)}
                        sx={{
                          border: "1px solid #E5E7EB",
                          width: 36,
                          height: 36,
                        }}
                      >
                        <img src={eye} alt="view" width={20} />
                      </IconButton>
                      <IconButton
                        onClick={() =>
                          handleDeleteDocument(COMBINED_DECEASED_DOCUMENT_KEY)
                        }
                        sx={{
                          border: "1px solid #FEE2E2",
                          width: 36,
                          height: 36,
                        }}
                      >
                        <img src={trash} alt="delete" width={18} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                {combinedDocumentError && (
                  <Typography color="error.main" fontSize={12} mt={1}>
                    {combinedDocumentError}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </Paper>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            py: 6,
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2 },
          }}
        >
          <ButtonComp
            variant="outlined"
            height={40}
            fontSize="16px"
            width={200}
            onClick={handleBack}
          >
            {string.claims.back}
          </ButtonComp>
          <ButtonComp
            variant="contained"
            height={40}
            fontSize="16px"
            width={200}
            onClick={handleNext}
          >
            {string.claims.next}
          </ButtonComp>
        </Box>
      </form>
    </LocalizationProvider>
  );
}

